import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as DV from "@impero-com/common/utils/dataView";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";

import type { UserProfile } from "api/generated/model";
import {
    Level,
    NodeType,
    ControlOverviewListDto,
    EntityContent,
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
    ControlOverview,
    ControlProgramOverview,
    EntityOverviewListDto,
    EntityView,
    SavedBlob,
} from "api/generated/model";
import { IdAndName } from "org/pageState/models";
import * as R from "org/risks/types";
import { Guid } from "utils/guid";

export const RISK_DIRECTORY_TAB = "risk_directory";
export const RISK_MAP_TAB = "risk_map";
export const RISK_MAP_INSIGHTS_TAB = "risk_map_insights";
export const RISK_MAP_INSIGHTS_TAB_OLD = "risk_map_insights_old";
export const RISK_MAP_HISTORY_TAB = "risk_map_history";
/*
 *
 * Riskmap history has been active since the 1st of Jan.
 * That means, all risk directories created before that date may have undefined (null) creationDate.
 * Our history, however cannot be limitless, therefore if the date is unknown,
 * then we set Riskmap History datepicker to be present from 2022-01-01 to present day.
 *
 */
export const RISK_MAP_HISTORY_FEATURE_AVAILABILITY = "2022-01-01";

/**
 * Expansion of a RiskNode for a given entity
 */
export interface ExpandedRiskNode {
    type: "ExpandedRiskNode";
    // Unique id
    // Warning: This is not a Guid
    id: string;
    // The risk node
    risk: R.RiskNode;
    // The entity id
    entityId: number;
    // The content for given risk & entity
    content: EntityContent | undefined;
}

/**
 * A node used in risk directory tab
 */
export type DirectoryNode = R.RiskDirectoryNode | R.ProcessNode | R.RiskNode;

/**
 * A node used in risk map tab
 */
export type MapNode = R.RiskDirectoryNode | R.ProcessNode | ExpandedRiskNode;

/**
 * Either a DirectoryNode, a MapNode or a NewRiskDirectoryNode, used in common functions
 */
export type AnyNode = DirectoryNode | MapNode | R.NewRiskDirectoryNode;

/**
 * Non-new node types with metadata
 */
export type ExistingNodeWithMD = R.ProcessNode | R.RiskNode | ExpandedRiskNode;

/**
 * All types of nodes with metadata
 */
export type NodeWithMD = R.NewRiskDirectoryNode | ExistingNodeWithMD;

/**
 * A descriptor of either a DirectoryNode or a MapNode, used in common functions
 *
 * Warning: This is different from DV.NodeDescriptor<AnyNode>
 */
export type AnyDescriptor =
    | DV.NodeDescriptor<DirectoryNode>
    | DV.NodeDescriptor<MapNode>;

/**
 * Props used by all tabs
 */
export interface CommonProps {
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
    riskDirectoryView: R.RiskDirectoryView;
    rootNode: RiskDirectoryNodeDtoWithChanges;
    entityList: EntityOverviewListDto;
    allControls: ControlOverviewListDto;
}

/**
 * Extra computed props for a node
 */
export type NodeComputedProps = Record<
    Guid,
    { parentId?: Guid; entityIds: number[] }
>;

/**
 * A metadata column level
 */
export type MetadataPrefix = "metadata";
export type MetadataColumnName = string;
export type MetadataColumnGroupingId =
    `${MetadataPrefix}_${Level}_${MetadataColumnName}`;
export type ProcessMetadataColumnId = Guid;
export type RiskMetadataColumnId = Guid;
export type MetadataColumnExtractorId =
    `${MetadataPrefix}_${Level}_${ProcessMetadataColumnId}_${RiskMetadataColumnId}_${MetadataColumnName}`;
export type FilterMetadataColumnExtractorId =
    `${NodeType}_${MetadataPrefix}_${Level}_${ProcessMetadataColumnId}_${MetadataColumnName}`;

/**
 * A metadata column, can be the merging of multiple columns with the same name (max. 1 per node type)
 */
export interface MetadataColumn {
    // Column name
    name: string;
    // Metadata fields for each types
    fields: Partial<Record<R.NodeType, R.MetadataField>>;
    // Position in section & section-columns, unique
    position?: number;
}

/**
 * Metadata column with its id following the pattern K.
 */
export type MetadataColumnWithId<K extends string> = MetadataColumn & { id: K };

/**
 * Map a normalized metadata column name following the pattern K with the matching metadata column
 */
export type MetadataColumnMap<K extends string> = Record<K, MetadataColumn>;

/**
 * Common getters exposed to tabs
 */
export interface CommonGetters {
    computedNodeProps: (rootNode: R.RiskDirectoryNode) => NodeComputedProps;
    metadataColumnMap: (
        view: R.RiskDirectoryView
    ) => Record<Level, MetadataColumnMap<MetadataColumnExtractorId>>;
    entities: (
        response: EntityOverviewListDto,
        rootNode: RiskDirectoryNodeDto
    ) => Record<number, EntityView>;
    metadataColumns: (
        riskDirectory: R.RiskDirectoryView
    ) => Record<Level, MetadataColumnWithId<MetadataColumnExtractorId>[]>;
    controlIdToControlAndActions: (
        rootNode: RiskDirectoryNodeDto,
        allControls: ControlOverviewListDto
    ) => Record<Guid, ControlAndActions>;
    getDescription: (node: AnyDescriptor) => string;
    getRisk: (node: AnyNode) => string | undefined;
    getProcesses: (node: AnyDescriptor) => R.ProcessNode[];
    getComputedNodeEntityIds: (nodeId: Guid) => number[];
    getComputedEntityIds: (nodeId: Guid) => number[];
    getDirectoryAncestorsEntitiesWithNodePaths: (
        ancestors: DirectoryNode[]
    ) => Record<number, string>;
    getDirectoryDescendantsEntitiesWithNodePaths: (
        desc: DV.NodeDescriptor<DirectoryNode>
    ) => Record<number, string[]>;
    getEntities: (desc: AnyDescriptor) => number[];
    renderEntities: (node: AnyDescriptor) => string;
}

/**
 * Perform a node update
 */
export type NodeUpdateAction<NODE> = (
    node: NODE
) => R.UpdatedRiskDirectoryNode | undefined;

/**
 * Scope of a node
 */
export type NodeScope =
    | {
          scope: "Global";
      }
    | {
          scope: "Local";
          entityId: number;
      };

export type OptionValueId = string;
export type SingleMetadataValueIdAndName = IdAndName<OptionValueId, string>;
export type MetadataValueForFilter =
    | string
    | SingleMetadataValueIdAndName
    | SingleMetadataValueIdAndName[];

/**
 * Associates a metadata field type and its value, if any
 */
export type MetadataInstance =
    | TextAreaInstance
    | TextInputInstance
    | NumericInputInstance
    | DropdownInstance
    | RadioButtonGroupInstance
    | CheckboxGroupInstance
    | HeadingInstance
    | AttachedFilesInstance
    | MetadataError;

export interface TextAreaInstance {
    type: "TextAreaInstance";
    fieldType: R.TextAreaField;
    value: string | undefined;
}

export interface TextInputInstance {
    type: "TextInputInstance";
    fieldType: R.TextInputField;
    value: string | undefined;
}

export interface NumericInputInstance {
    type: "NumericInputInstance";
    fieldType: R.NumericInputField;
    value: number | undefined;
}

export interface DropdownInstance {
    type: "DropdownInstance";
    fieldType: R.DropdownField;
    value: R.OptionValue | undefined;
}

export interface RadioButtonGroupInstance {
    type: "RadioButtonGroupInstance";
    fieldType: R.RadioButtonGroupField;
    value: R.OptionValue | undefined;
}

export interface CheckboxGroupInstance {
    type: "CheckboxGroupInstance";
    fieldType: R.CheckboxGroupField;
    value: R.OptionValue[] | undefined;
}

export interface HeadingInstance {
    type: "HeadingInstance";
    fieldType: R.HeadingField;
}

export interface MetadataError {
    type: "MetadataError";
    error: "FormulaError";
}

export interface AttachedFilesInstance {
    type: "AttachedFilesInstance";
    fieldType: R.AttachedFilesField;
    value: SavedBlob[] | undefined;
}

export type DataViewIndexValue =
    | {
          type: "OptionValue";
          value: R.OptionValue;
      }
    | {
          type: "AttachedFile";
          value: SavedBlob;
      };

/**
 * Represent all different node creation permission cases
 * where creation is allowed
 */
export type NodeCreationAllowedPermission =
    | GlobalOnlyPermission
    | GlobalAndLocalPermission
    | LocalOnlyPermission
    | ChildOfLocalNodePermission;

/**
 * Represent all different node creation permission cases
 */
export type NodeCreationPermission =
    | NodeCreationAllowedPermission
    | NoPermission;

export interface GlobalOnlyPermission {
    type: "GlobalOnlyPermission";
}

export interface GlobalAndLocalPermission {
    type: "GlobalAndLocalPermission";
    entityIds: number[]; // MUST NOT BE EMPTY
}

export interface LocalOnlyPermission {
    type: "LocalOnlyPermission";
    entityIds: number[]; // MUST NOT BE EMPTY
}

export interface ChildOfLocalNodePermission {
    type: "ChildOfLocalNodePermission";
    entityId: number;
}

export interface NoPermission {
    type: "NoPermission";
}

export interface ControlAndActions {
    control: ControlOverview;
    controlProgram: ControlProgramOverview;
    controlAction: ActionType;
    controlProgramAction: ActionType;
}

export const Action = {
    EDIT: "Edit" as const,
    VIEW: "View" as const,
};
export type ActionType = typeof Action.EDIT | typeof Action.VIEW | null;

export interface RiskDirectoryNodeDtoWithChanges extends RiskDirectoryNodeDto {
    lastChanges?: ChangeList;
}

export interface ChangeList {
    inserted?: DirectoryNode[];
    updated?: DirectoryNode[];
    deleted?: Set<Guid>;
}
export type FocusedEntity =
    | { type: "FOCUSED"; entityId: number }
    | { type: "NONE" };
